/**
 * Turning the reports API into chart input.
 *
 * `GET /reports/*` answers in buckets (`[{ key, label, count }]`), a sparse
 * per-day series and percentile blocks. Charts want dense, ordered, named rows,
 * and the dashboard wants a single number. Every one of those conversions lives
 * here so the two pages that draw reports agree on what a figure means.
 */
import type { CountBucket, ReportDuration, ReportSummary, Severity } from '../../lib/schemas.js';

const DAY_MS = 86_400_000;

/** Buckets as a lookup; `key: null` ("not set") is folded under `''`. */
export function bucketMap(buckets: CountBucket[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const bucket of buckets) out[bucket.key ?? ''] = bucket.count;
  return out;
}

export function bucketCount(buckets: CountBucket[], key: string): number {
  return bucketMap(buckets)[key] ?? 0;
}

export interface DayPoint {
  date: string;
  count: number;
}

/**
 * The per-day series with the quiet days put back.
 *
 * The API omits days on which nothing happened. Plotting the sparse series
 * would draw a straight line across an outage-free week and make it look busy,
 * so the gaps are filled with zeroes across the report's own range.
 */
export function densePerDay(summary: ReportSummary | undefined): DayPoint[] {
  if (!summary) return [];
  const counts = new Map(summary.per_day.map((row) => [row.day, row.count]));
  const from = Date.parse(summary.range.from);
  const to = Date.parse(summary.range.to);
  if (!Number.isFinite(from) || !Number.isFinite(to) || to < from) {
    return summary.per_day.map((row) => ({ date: row.day, count: row.count }));
  }
  const out: DayPoint[] = [];
  const start = Date.UTC(
    new Date(from).getUTCFullYear(),
    new Date(from).getUTCMonth(),
    new Date(from).getUTCDate(),
  );
  for (let day = start; day <= to; day += DAY_MS) {
    const key = new Date(day).toISOString().slice(0, 10);
    out.push({ date: key, count: counts.get(key) ?? 0 });
    // A very long range would make an unreadable chart and a huge loop; the API
    // caps ranges at 400 days, and so does this.
    if (out.length >= 400) break;
  }
  return out;
}

export const SEVERITY_ORDER: Severity[] = ['critical', 'major', 'warning', 'info'];

/** Alarms raised in the period, by severity, always in severity order. */
export function severityCounts(summary: ReportSummary | undefined): Record<Severity, number> {
  const map = summary ? bucketMap(summary.by_severity) : {};
  return {
    critical: map.critical ?? 0,
    major: map.major ?? 0,
    warning: map.warning ?? 0,
    info: map.info ?? 0,
  };
}

export interface StateCounts {
  open: number;
  acknowledged: number;
  snoozed: number;
  closed: number;
  total: number;
}

export function stateCounts(summary: ReportSummary | undefined): StateCounts {
  const map = summary ? bucketMap(summary.by_state) : {};
  return {
    open: map.open ?? 0,
    acknowledged: map.acknowledged ?? 0,
    snoozed: map.snoozed ?? 0,
    closed: map.closed ?? 0,
    total: summary?.total ?? 0,
  };
}

/** The headline figure of an MTTA/MTTR report: the mean, in seconds. */
export function meanSeconds(report: ReportDuration | undefined): number | null {
  return report?.overall.mean_seconds ?? null;
}

export function measuredCount(report: ReportDuration | undefined): number {
  return report?.overall.count ?? 0;
}

/**
 * Yesterday against the day before, from the same dense series. The API has no
 * "previous period" figure, so the delta on the dashboard is computed here from
 * the two most recent whole days rather than being invented.
 */
export function lastTwoDays(points: DayPoint[]): { latest: number; previous: number } {
  return { latest: points.at(-1)?.count ?? 0, previous: points.at(-2)?.count ?? 0 };
}
